/**
 * Catch-all for a URL that matches no route.
 *
 * The app had no `path="*"` route, so an unmatched URL rendered NOTHING —
 * a white page with no header, no message and no way back. That is how a
 * wrong link in a notification reached users: the overdue-training e-mail
 * pointed at /training/{id} (the real route is /training/records/{id}), and
 * clicking it produced a blank screen rather than anything that said so.
 *
 * The link itself is fixed; this exists so the next wrong link — a stale
 * notification already in someone's inbox, a bookmark from a renamed route,
 * a typo — is legible instead of looking like an outage.
 */
import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Compass, ArrowLeft } from 'lucide-react';
import TabNavigation from '../components/TabNavigation';

const NotFound = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* The header is deliberately present: someone who lands here from a
          stale link still needs the site switcher and the menus. */}
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-20 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gray-100">
          <Compass className="h-7 w-7 text-gray-400" />
        </span>

        <h1 className="mt-6 text-2xl font-bold text-gray-900">This page doesn’t exist</h1>
        <p className="mt-2 text-sm text-gray-600">
          The link you followed points somewhere this app has no page for. If you
          got here from a notification or a bookmark, it is probably out of date.
        </p>

        <code className="mt-4 max-w-full truncate rounded-lg bg-gray-100 px-3 py-1.5
                         text-xs text-gray-500" title={pathname}>
          {pathname}
        </code>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white
                       px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" /> Go back
          </button>
          <Link
            to="/"
            className="rounded-lg bg-[#1B5C94] px-4 py-2.5 text-sm font-semibold text-white
                       hover:bg-[#164b79]"
          >
            Go to Dashboard
          </Link>
          <Link
            to="/safety"
            className="px-2 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-800"
          >
            Safety Management
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
